'use client';

import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useInView } from 'react-intersection-observer';
import { motion } from 'motion/react';
import type { SanityImage } from '../types';
import { Media } from '../Media';
import { Anchor } from '../Anchor';

export const defaultActiveHotspotIndex = undefined;
const activeHotspotIndexAtom = atom<number | undefined>(defaultActiveHotspotIndex);

export function useActiveHotspotIndexValue() {
	const activeHotspotIndex = useAtomValue(activeHotspotIndexAtom);
	return activeHotspotIndex;
}

export function useSetActiveHotspotIndex() {
	const setActiveHotspotIndex = useSetAtom(activeHotspotIndexAtom);
	return (activeHotspotIndex: number | undefined) => setActiveHotspotIndex(activeHotspotIndex);
}

export type HotspotsProps = {
	className?: string;
	hotspots: HotspotData[];
	showCardOnHover?: boolean;
	inViewOverride?: boolean;
};

export function Hotspots(props: HotspotsProps) {
	const showCardOnHover = props.showCardOnHover ?? true;
	const { ref, inView } = useInView({
		threshold: 0.75,
		triggerOnce: true,
	});
	const triggerInviewAnimation = props.inViewOverride !== undefined ? props.inViewOverride : inView;
	const notInViewOverride = props.inViewOverride === false;

	return (
		<div className={`absolute top-0 left-0 right-0 bottom-0 z-20`} ref={ref}>
			{props?.hotspots.map(function (hotspot, index) {
				return (
					<Hotspot
						key={`hotspot-${index}-${hotspot.x}-${hotspot.y}`}
						hotspot={hotspot}
						showCardOnHover={showCardOnHover}
						notInViewOverride={notInViewOverride}
						triggerInviewAnimation={triggerInviewAnimation}
						index={index}
					/>
				);
			})}
		</div>
	);
}

export type HotspotData = {
	data: {
		image: SanityImage;
		href: string;
	};
	x: number;
	y: number;
};

export type HotspotProps = {
	hotspot: HotspotData;
	showCardOnHover: boolean;
	notInViewOverride: boolean;
	triggerInviewAnimation: boolean;
	index: number;
};

function Hotspot(props: HotspotProps) {
	return (
		<motion.div
			className='absolute -translate-x-1/2 -translate-y-1/2 duration-fast ease-smooth scale-50 hover:scale-[.55] @min-[25rem]:scale-[.66] @min-[25rem]:hover:scale-[.72] @min-[31.25rem]:scale-[.82] @min-[31.25rem]:hover:scale-[.9] @min-[37.5rem]:scale-100 @min-[37.5rem]:hover:scale-110'
			style={{ top: `${props.hotspot.y}%`, left: `${props.hotspot.x}%` }}
			animate={{ scale: props.triggerInviewAnimation ? 1 : 0 }}
			transition={{
				delay: props.notInViewOverride ? 0 : 0.1 * props.index,
				duration: props.notInViewOverride ? 0 : 0.5,
				ease: [0.165, 0.84, 0.44, 1],
			}}
		>
			<Anchor aria-label={`Scroll to ${props.hotspot.data.href} section`} href={props.hotspot.data.href}>
				<div
					style={{
						height: `${props.hotspot.data.image.asset?.metadata?.dimensions?.height / 2}px`,
						width: `${props.hotspot.data.image.asset?.metadata?.dimensions?.width / 2}px`,
					}}
				>
					<Media
						className={`h-full [&_img]:h-full [&_img]:object-fit! [&_img]:max-w-none! [&_img]:w-auto!`}
						image={props.hotspot.data.image}
					/>
				</div>
			</Anchor>
		</motion.div>
	);
}
